// api-auth.js — attaches the caller's Clerk session token to our own /api routes.
//
// Why this exists separately from lib/supabase.js:
//
// supabase.js also holds a Clerk token getter, but it stores one that returns a
// JWT minted from Clerk's "supabase" template — signed with Supabase's JWT secret
// so Postgres RLS can verify it. Our serverless routes verify with
// CLERK_SECRET_KEY instead (see api/_auth.js), and a supabase-template token will
// NOT validate there. So this module keeps the *default* session token, and both
// getters are wired up side by side in components/Auth.jsx.
//
// Routes that need this: /api/fellow, /api/sonos, /api/bottomless. They proxy the
// owner's real hardware and third-party accounts, so api/_auth.js#requireOwner
// rejects anything without a valid owner token.
//
// (/api/vision and /api/brew-recipe solve the same problem by threading getToken
// through each call — see lib/morning-ai.js. That works because they're called
// from a couple of React components; aiden/sonos/bottomless are called from a
// dozen places, so a module-level getter avoids plumbing it through all of them.)

// null means "not wired yet" — distinct from "wired but signed out", which is a
// getter that resolves to null. The distinction matters: see waitForGetter.
let sessionTokenGetter = null;
let waiters = [];
// Once Clerk has wired a getter at least once, we never wait again. Otherwise
// signing out (which clears the getter) would make every subsequent call stall
// for the full timeout before failing.
let everWired = false;

export function setSessionTokenGetter(fn) {
  sessionTokenGetter = fn || null;
  if (fn) {
    everWired = true;
    // Release anything that started before Clerk finished loading.
    if (waiters.length) {
      const pending = waiters;
      waiters = [];
      pending.forEach((release) => release());
    }
  }
}

// Test seam — resets module state between cases.
export function __resetApiAuth() {
  sessionTokenGetter = null;
  waiters = [];
  everWired = false;
}

// How long to wait for Clerk to wire the getter before giving up and sending
// the request bare (which will 401, but at least fails fast rather than hanging).
const GETTER_WAIT_MS = 8000;

/**
 * Wait until the token getter exists.
 *
 * Why this is needed: components call these APIs on mount, which happens before
 * Clerk has loaded and before Auth.jsx wires the getter. Firing immediately
 * produced an unauthenticated request, a 401, and — because callers like
 * sonos.js `listGroups()` swallow errors and return [] — a silently empty UI
 * that never retried. That is exactly how the Sonos card broke: the request
 * went out with no Authorization header at all, one time, before sign-in.
 */
function waitForGetter() {
  // Wired now, or wired before and since cleared by sign-out — either way,
  // don't wait.
  if (sessionTokenGetter || everWired) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      waiters = waiters.filter((w) => w !== release);
      resolve();
    }, GETTER_WAIT_MS);
    const release = () => { clearTimeout(timer); resolve(); };
    waiters.push(release);
  });
}

/**
 * fetch() against one of our own /api routes with the Clerk session token
 * attached. Same signature as fetch; returns the raw Response so callers keep
 * their existing status/error handling.
 */
export async function apiFetch(path, init = {}) {
  await waitForGetter();
  const token = sessionTokenGetter ? await sessionTokenGetter().catch(() => null) : null;
  const headers = new Headers(init.headers || {});
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return fetch(path, { ...init, headers });
}
